/**
 * AuthGate — auth enforcement.
 *
 * Behavior controlled by VITE_REQUIRE_AUTH:
 *   - "true" (Phase 13 production default): redirects unauthenticated users to /login
 *   - "false": renders children regardless of auth state (localStorage fallback)
 *
 * Wraps the routes that should be protected. When auth is required but the
 * initial auth check is still in flight, shows a brief loading state.
 */

import { Navigate, useLocation } from 'react-router-dom';
import { type ReactNode } from 'react';
import { useAuthStore } from '@/store/useAuthStore';

const requireAuth = import.meta.env.VITE_REQUIRE_AUTH === 'true';

interface AuthGateProps {
  children: ReactNode;
}

export function AuthGate({ children }: AuthGateProps) {
  const user = useAuthStore((s) => s.user);
  const isLoading = useAuthStore((s) => s.isLoading);
  const location = useLocation();

  // If auth isn't required, never block.
  if (!requireAuth) return <>{children}</>;

  // While the initial auth check is in flight, show a brief loading state.
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-surface-canvas">
        <div className="text-sm text-ink-tertiary">Loading…</div>
      </div>
    );
  }

  // Auth required and user not logged in → bounce to login.
  if (!user) {
    return <Navigate to="/login" state={{ from: location.pathname }} replace />;
  }

  return <>{children}</>;
}

/** True iff the auth gate is currently enforcing. Useful for header logic. */
export const isAuthRequired = requireAuth;
